/**
 * The service exchange page's address — `/inseason/service-exchange?forecastRun=&measure=`
 * (bata-ui issue 308, `docs/adr/6015`). The URL is the state (G2.2, CLAUDE.md §3.4): the forecast
 * run the backtest is of, and which service measure the curve shows. Pure: the parse takes the
 * search string, so the tests and the export resolver hand it one.
 *
 * `forecastRun` has NO default here. With none in the URL the page opens the newest evaluated
 * forecast run through the run-default machinery (DEC-99,
 * ADR 9039), which writes it INTO the URL; it never reads without one. `measure` is a view of the
 * same read, not a scope: absent, the curve shows the fill rate.
 */

export const SERVICE_EXCHANGE_PATH = '/inseason/service-exchange';
export const FORECAST_RUN_PARAM = 'forecastRun';
export const MEASURE_PARAM = 'measure';

/** The two measures the curve shows: the share of pairs served, the share of sizes fully served. */
export type ExchangeMeasure = 'fillRate' | 'cycleService';

/** The `?measure=` value of the cycle service view; the fill rate has none. */
export const CYCLE_SERVICE_VALUE = 'cycle';

export interface ExchangeScope {
  readonly forecastRunId: string | null;
  readonly measure: ExchangeMeasure;
}

export function readExchangeScope(search: string): ExchangeScope {
  const params = new URLSearchParams(search);
  const run = params.get(FORECAST_RUN_PARAM)?.trim() ?? '';
  return {
    forecastRunId: run === '' ? null : run,
    measure: params.get(MEASURE_PARAM) === CYCLE_SERVICE_VALUE ? 'cycleService' : 'fillRate',
  };
}

/** The search string with the measure set — the fill rate drops the parameter. Pure. */
export function withMeasure(search: string, measure: ExchangeMeasure): string {
  const params = new URLSearchParams(search);
  if (measure === 'cycleService') params.set(MEASURE_PARAM, CYCLE_SERVICE_VALUE);
  else params.delete(MEASURE_PARAM);
  return params.toString();
}

export function exchangeHref(forecastRunId: string | null): string {
  return forecastRunId === null ? SERVICE_EXCHANGE_PATH : `${SERVICE_EXCHANGE_PATH}?${FORECAST_RUN_PARAM}=${encodeURIComponent(forecastRunId)}`;
}
